import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { NexusIcon } from '../shared/ui/NexusIcon';
import { Btn, Pill, SearchInput } from '../shared/ui';
import { useToast } from '../shared/feedback';
import { PageHead, StatCard, TenancyModal } from '../platform/_ui';
import { orgWorkspaces, statusTone } from '../platform/platformStore';
import { orgChannelsApi } from '../api/org-channels.api.js';
import { usePermissions } from './auth/usePermissions.js';
import { DeniedState } from './auth/DeniedState.jsx';
import { useOrgRuntime } from './OrganizationRuntimeContext.jsx';

/* Backend OrgChannelType enum (uppercase) → display definition */
const CHANNEL_DEFS = [
  { id: 'whatsapp',  type: 'WHATSAPP',  name: 'WhatsApp',       icon: 'MessageCircle', color: '#25D366', category: 'messaging'  },
  { id: 'facebook',  type: 'FACEBOOK',  name: 'Facebook',        icon: 'Facebook',      color: '#1877F2', category: 'social'     },
  { id: 'instagram', type: 'INSTAGRAM', name: 'Instagram',       icon: 'Instagram',     color: '#E4405F', category: 'social'     },
  { id: 'telegram',  type: 'TELEGRAM',  name: 'Telegram',        icon: 'Send',          color: '#26A5E4', category: 'messaging'  },
  { id: 'slack',     type: 'SLACK',     name: 'Slack',           icon: 'Hash',          color: '#4A154B', category: 'workspace'  },
  { id: 'teams',     type: 'TEAMS',     name: 'Microsoft Teams', icon: 'Users',         color: '#6264A7', category: 'workspace'  },
  { id: 'webchat',   type: 'WEB_CHAT',  name: 'Web Chat',        icon: 'Globe',         color: 'var(--nx-accent, #125AB0)', category: 'web' },
  { id: 'email',     type: 'EMAIL',     name: 'Email',           icon: 'Mail',          color: 'var(--nx-accent)', category: 'email'    },
  { id: 'voice',     type: 'VOICE',     name: 'Voice',           icon: 'Phone',         color: 'var(--nx-accent)', category: 'voice'    },
  { id: 'sms',       type: 'SMS',       name: 'SMS',             icon: 'Smartphone',    color: 'var(--nx-accent)', category: 'messaging'},
];

const STATUS_OPTS = ['All', 'CONNECTED', 'DISCONNECTED', 'ERROR', 'PENDING'];
const CAT_OPTS    = ['All', 'messaging', 'social', 'workspace', 'web', 'email', 'voice'];

const CH_COLOR = { healthy: 'var(--nx-success)', unknown: 'var(--nx-warning)', unhealthy: 'var(--nx-danger)' };
const CH_TONE  = { healthy: 'green', unknown: 'amber', unhealthy: 'red' };

const EMPTY_FORM = { apiKey: '', secret: '', webhookUrl: '', workspace: '', smtpHost: '', smtpPort: '587', smtpUser: '', smtpPass: '' };

/* ── Pure helpers (outside component — don't count toward component complexity) ── */

function chHealth(ch) {
  if (ch.status === 'ERROR' || ch.lastError) return 'unhealthy';
  if (ch.status === 'PENDING' || !ch.lastHealthAt) return 'unknown';
  return 'healthy';
}

function connPillTone(status) {
  if (status === 'CONNECTED')    return 'green';
  if (status === 'DISCONNECTED') return 'gray';
  return 'amber';
}

function filterDefs(channels, q, statusF, catF) {
  return CHANNEL_DEFS.filter(def => {
    const conn   = channels.find(c => c.type === def.type);
    const matchQ = !q || def.name.toLowerCase().includes(q.toLowerCase());
    const matchS = statusF === 'All' || (conn ? conn.status : 'DISCONNECTED') === statusF;
    const matchC = catF === 'All' || def.category === catF;
    return matchQ && matchS && matchC;
  });
}

/* ── Channel card ─────────────────────────────────────────────────────────── */

function ChannelCard({ def, conn, onDetails, onEdit, onTest, onDisconnect, onReconnect }) {
  const isConnected = conn?.status === 'CONNECTED';
  const isDisconn   = conn?.status === 'DISCONNECTED';
  const h           = conn ? chHealth(conn) : null;

  return (
    <div className="nx-cc-chart-card" style={{ padding: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
        <div style={{ '--_cc': def.color, width: 44, height: 44, borderRadius: 10, background: 'color-mix(in srgb, var(--_cc) 9%, transparent)', color: 'var(--_cc)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <NexusIcon name={def.icon} size={20} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: 14 }}>{def.name}</div>
          <div style={{ fontSize: 11, color: 'var(--nx-text-muted)', textTransform: 'capitalize' }}>{def.category}</div>
        </div>
        <Pill tone={conn ? connPillTone(conn.status) : 'gray'}>
          {conn?.status ?? 'Not connected'}
        </Pill>
      </div>

      {conn && h && (
        <div style={{ marginBottom: 10, display: 'flex', gap: 8, fontSize: 12, color: 'var(--nx-text-muted)' }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <div style={{ width: 7, height: 7, borderRadius: '50%', background: CH_COLOR[h] }} />
            {h}
          </span>
          {conn.webhookUrl && <span>Webhook configured</span>}
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
        {!conn && (
          <Btn variant="primary" size="sm" icon="Wifi" style={{ flex: 1 }} onClick={onEdit}>Connect</Btn>
        )}
        {isConnected && <>
          <Btn variant="ghost" size="sm" icon="Eye"    onClick={onDetails}>Details</Btn>
          <Btn variant="ghost" size="sm" icon="Pencil" onClick={onEdit}>Edit</Btn>
          <Btn variant="ghost" size="sm" icon="Zap"    onClick={onTest}>Test</Btn>
          <Btn variant="ghost" size="sm" icon="X"      onClick={onDisconnect}>Disconnect</Btn>
        </>}
        {isDisconn && <>
          <Btn variant="primary" size="sm" icon="RefreshCw" onClick={onReconnect}>Reconnect</Btn>
          <Btn variant="ghost"   size="sm" icon="Pencil"    onClick={onEdit}>Edit</Btn>
        </>}
      </div>
    </div>
  );
}

/* ── Main page ────────────────────────────────────────────────────────────── */

export default function OrgChannels({ data }) {
  const { orgId }  = useParams();
  const navigate   = useNavigate();
  const [searchParams] = useSearchParams();
  const returnTo    = searchParams.get('returnTo');
  const returnLabel = searchParams.get('returnLabel') || 'Back';
  const toast       = useToast();
  const perm                        = usePermissions();
  const { loading: runtimeLoading } = useOrgRuntime();

  const [q, setQ]                           = useState('');
  const [statusF, setStatusF]               = useState('All');
  const [catF, setCatF]                     = useState('All');
  const [channels, setChannels]             = useState([]);
  const [loading, setLoading]               = useState(true);
  const [connectModal, setConnectModal]     = useState(null);
  const [detailModal, setDetailModal]       = useState(null);
  const [deleteConfirm, setDeleteConfirm]   = useState(null);
  const [disconnectConfirm, setDisconnectConfirm] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setChannels((await orgChannelsApi.list(orgId)) || []);
    } catch {
      toast({ tone: 'error', icon: 'AlertCircle', title: 'Failed to load channels' });
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => { load(); }, [load]);

  const org = data.state.organizations.find(o => o.id === orgId);
  const wss = orgWorkspaces(data.state, orgId);
  if (!org || runtimeLoading) return null;

  if (!perm.flags.canManageChannels && !perm.isSA) {
    return (
      <div className="nx-cc-page">
        <DeniedState reason="insufficient_permission" message="Channel management requires Integration Manager permissions." />
      </div>
    );
  }

  const connected    = channels.filter(c => c.status === 'CONNECTED');
  const healthy      = channels.filter(c => chHealth(c) === 'healthy');
  const disconnected = channels.filter(c => c.status === 'DISCONNECTED');
  const filteredDefs = filterDefs(channels, q, statusF, catF);

  /* ── Handlers ──────────────────────────────────────────────────────────── */

  const openConnectModal = (def) => { setForm(EMPTY_FORM); setConnectModal(def); };

  const openEditModal = (conn, def) => {
    const base = def.id === 'email'
      ? { ...EMPTY_FORM, workspace: conn.workspaceId || '', smtpHost: conn.config?.host || '', smtpPort: String(conn.config?.port || '587'), smtpUser: conn.config?.username || '' }
      : { ...EMPTY_FORM, webhookUrl: conn.webhookUrl || '', workspace: conn.workspaceId || '' };
    setForm(base);
    setConnectModal(def);
  };

  const handleConnect = async () => {
    if (!connectModal) return;
    const existingConn = channels.find(c => c.type === connectModal.type);
    const isEmail = connectModal.id === 'email';
    const payload = isEmail
      ? { type: connectModal.type, name: connectModal.name, config: { host: form.smtpHost, port: Number(form.smtpPort) || 587, username: form.smtpUser, password: form.smtpPass || undefined }, workspaceId: form.workspace || null }
      : { type: connectModal.type, name: connectModal.name, apiKey: form.apiKey || undefined, secret: form.secret || undefined, webhookUrl: form.webhookUrl || undefined, workspaceId: form.workspace || null };
    try {
      if (existingConn) {
        await orgChannelsApi.update(orgId, existingConn.id, payload);
      } else {
        await orgChannelsApi.connect(orgId, payload);
      }
      await load();
      toast({ tone: 'success', icon: 'Check', title: (existingConn ? connectModal.name + ' updated' : connectModal.name + ' connected') });
      setConnectModal(null);
      setForm(EMPTY_FORM);
    } catch (e) {
      toast({ tone: 'error', icon: 'AlertCircle', title: existingConn ? 'Update failed' : 'Connection failed', desc: e?.message });
    }
  };

  const handleTest = async (ch) => {
    try {
      await orgChannelsApi.test(orgId, ch.id);
      await load();
      toast({ tone: 'success', icon: 'CheckCircle', title: 'Connection test passed', desc: ch.name || ch.type });
    } catch (e) {
      toast({ tone: 'error', icon: 'AlertCircle', title: 'Connection test failed', desc: e?.message });
    }
  };

  const handleDisconnectConfirmed = async () => {
    const ch = disconnectConfirm;
    if (!ch) return;
    try {
      await orgChannelsApi.disconnect(orgId, ch.id);
      await load();
      toast({ tone: 'warning', icon: 'X', title: (ch.name || ch.type) + ' disconnected' });
      setDisconnectConfirm(null);
      setDetailModal(null);
    } catch (e) {
      toast({ tone: 'error', icon: 'AlertCircle', title: 'Failed to disconnect', desc: e?.message });
    }
  };

  const handleReconnect = async (ch) => {
    try {
      await orgChannelsApi.reconnect(orgId, ch.id);
      await load();
      toast({ tone: 'success', icon: 'RefreshCw', title: (ch.name || ch.type) + ' reconnected' });
    } catch (e) {
      toast({ tone: 'error', icon: 'AlertCircle', title: 'Reconnect failed', desc: e?.message });
    }
  };

  const handleDelete = async () => {
    try {
      await orgChannelsApi.remove(orgId, deleteConfirm.id);
      await load();
      toast({ tone: 'warning', icon: 'Trash', title: 'Channel removed' });
      setDeleteConfirm(null);
      setDetailModal(null);
    } catch (e) {
      toast({ tone: 'error', icon: 'AlertCircle', title: 'Remove failed', desc: e?.message });
    }
  };

  /* ── Derived form state ──────────────────────────────────────────────────── */

  const isEdit      = !!connectModal && channels.some(c => c.type === connectModal.type);
  const isEmailForm = connectModal?.id === 'email';

  /* ── Render ─────────────────────────────────────────────────────────────── */

  return (
    <div className="nx-cc-page">
      <PageHead
        title="Channels"
        sub={'Communication channels for ' + org.name}
        back={returnTo ? { label: `Back to ${returnLabel}`, onClick: () => navigate(returnTo) } : undefined}
        actions={<Btn variant="ghost" icon="RefreshCw" disabled={loading} onClick={load}>Refresh</Btn>}
      />

      <div className="nx-cc-stat-grid nx-kpi-4">
        <StatCard label="Connected"    value={connected.length}    icon="Wifi"        color="green"  />
        <StatCard label="Healthy"      value={healthy.length}      icon="CheckCircle" color="blue"   />
        <StatCard label="Disconnected" value={disconnected.length} icon="WifiOff"     color="amber"  />
        <StatCard label="Available"    value={CHANNEL_DEFS.length} icon="LayoutGrid"  color="purple" />
      </div>

      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <SearchInput value={q} onChange={e => setQ(e.target.value)} placeholder="Search channels…" width={240} />
        <select className="nx-input" style={{ width: 160, fontSize: 13 }} value={statusF} onChange={e => setStatusF(e.target.value)}>
          {STATUS_OPTS.map(s => <option key={s}>{s}</option>)}
        </select>
        <select className="nx-input" style={{ width: 150, fontSize: 13 }} value={catF} onChange={e => setCatF(e.target.value)}>
          {CAT_OPTS.map(c => <option key={c}>{c}</option>)}
        </select>
      </div>

      {loading && channels.length === 0 && (
        <div className="nx-empty"><NexusIcon name="Loader" size={20} /><span>Loading channels…</span></div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 14 }}>
        {filteredDefs.map(def => {
          const conn = channels.find(c => c.type === def.type);
          return (
            <ChannelCard
              key={def.id}
              def={def}
              conn={conn}
              onDetails={() => setDetailModal(conn)}
              onEdit={() => conn ? openEditModal(conn, def) : openConnectModal(def)}
              onTest={() => handleTest(conn)}
              onDisconnect={() => setDisconnectConfirm(conn)}
              onReconnect={() => handleReconnect(conn)}
            />
          );
        })}
      </div>

      {!loading && filteredDefs.length === 0 && (
        <div className="nx-empty"><NexusIcon name="Wifi" size={22} /><span>No channels match your filters.</span></div>
      )}

      {/* ── Connect / Edit modal ── */}
      {connectModal && (
        <TenancyModal
          title={(isEdit ? 'Edit ' : 'Connect ') + connectModal.name}
          icon={connectModal.icon}
          onClose={() => { setConnectModal(null); setForm(EMPTY_FORM); }}
          width={520}
        >
          <div className="nx-form" style={{ gap: 10, marginTop: 8 }}>
            {isEdit && (
              <div style={{ padding: '6px 10px', background: 'var(--nx-surface-tint, rgba(0,0,0,.04))', borderRadius: 6, fontSize: 12, color: 'var(--nx-text-muted)', marginBottom: 4 }}>
                Leave credential fields blank to keep existing values.
              </div>
            )}
            {isEmailForm ? (
              <>
                <div className="nx-form-row">
                  <label htmlFor="ch-smtp-host">SMTP Host <span style={{ color: 'var(--nx-danger)' }}>*</span></label>
                  <input id="ch-smtp-host" className="nx-input" autoFocus value={form.smtpHost} onChange={e => setForm(f => ({ ...f, smtpHost: e.target.value }))} placeholder="smtp.example.com" />
                </div>
                <div className="nx-form-row">
                  <label htmlFor="ch-smtp-port">SMTP Port</label>
                  <input id="ch-smtp-port" className="nx-input" type="number" value={form.smtpPort} onChange={e => setForm(f => ({ ...f, smtpPort: e.target.value }))} style={{ width: 100 }} />
                </div>
                <div className="nx-form-row">
                  <label htmlFor="ch-smtp-user">
                    Username {!isEdit && <span style={{ color: 'var(--nx-danger)' }}>*</span>}
                  </label>
                  <input id="ch-smtp-user" className="nx-input" value={form.smtpUser} onChange={e => setForm(f => ({ ...f, smtpUser: e.target.value }))} placeholder="user@example.com" />
                </div>
                <div className="nx-form-row">
                  <label htmlFor="ch-smtp-pass">Password</label>
                  <input id="ch-smtp-pass" className="nx-input" type="password" autoComplete="new-password" value={form.smtpPass} onChange={e => setForm(f => ({ ...f, smtpPass: e.target.value }))} placeholder={isEdit ? 'Leave blank to keep existing' : 'SMTP password or app password'} />
                </div>
              </>
            ) : (
              <>
                <div className="nx-form-row">
                  <label htmlFor="ch-api-key">API Key / Access Token</label>
                  <input id="ch-api-key" className="nx-input" type="password" autoFocus autoComplete="new-password" value={form.apiKey} onChange={e => setForm(f => ({ ...f, apiKey: e.target.value }))} placeholder={isEdit ? 'Leave blank to keep existing' : 'Paste your API key or access token'} />
                </div>
                <div className="nx-form-row">
                  <label htmlFor="ch-secret">Secret / App Secret</label>
                  <input id="ch-secret" className="nx-input" type="password" autoComplete="new-password" value={form.secret} onChange={e => setForm(f => ({ ...f, secret: e.target.value }))} placeholder={isEdit ? 'Leave blank to keep existing' : 'App secret or webhook verify token'} />
                </div>
                <div className="nx-form-row">
                  <label htmlFor="ch-webhook">Webhook URL</label>
                  <input id="ch-webhook" className="nx-input" value={form.webhookUrl} onChange={e => setForm(f => ({ ...f, webhookUrl: e.target.value }))} placeholder="https://your-webhook-endpoint.com/…" />
                </div>
              </>
            )}
            <div className="nx-form-row">
              <label htmlFor="ch-workspace">Map to Workspace</label>
              <select id="ch-workspace" className="nx-input" value={form.workspace} onChange={e => setForm(f => ({ ...f, workspace: e.target.value }))}>
                <option value="">All Workspaces</option>
                {wss.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
              </select>
            </div>
          </div>
          <div className="nx-confirm-actions" style={{ marginTop: 14 }}>
            <Btn variant="ghost" onClick={() => { setConnectModal(null); setForm(EMPTY_FORM); }}>Cancel</Btn>
            <Btn variant="primary" icon="Wifi"
              disabled={isEmailForm ? !form.smtpHost.trim() : (!isEdit && !form.apiKey.trim())}
              onClick={handleConnect}>
              {isEdit ? 'Save Changes' : 'Connect Channel'}
            </Btn>
          </div>
        </TenancyModal>
      )}

      {/* ── Detail modal ── */}
      {detailModal && (() => {
        const dh     = chHealth(detailModal);
        const wsName = detailModal.workspaceId
          ? (wss.find(w => w.id === detailModal.workspaceId)?.name ?? detailModal.workspaceId)
          : 'All Workspaces';
        const connAt = detailModal.connectedAt
          ? new Date(detailModal.connectedAt).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })
          : '—';
        return (
          <TenancyModal title={(detailModal.name || 'Channel') + ' — Details'} icon="Wifi" onClose={() => setDetailModal(null)} width={480}>
            <div className="nx-form" style={{ marginTop: 10 }}>
              {[
                ['Status',       <Pill key="st" tone={statusTone(detailModal.status)}>{detailModal.status}</Pill>],
                ['Health',       <Pill key="hl" tone={CH_TONE[dh]}>{dh}</Pill>],
                ['Channel Type', detailModal.type],
                ['Workspace',    wsName],
                ['Webhook URL',  detailModal.webhookUrl || '—'],
                ['Connected',    connAt],
              ].map(([label, value]) => (
                <div key={label} className="nx-form-row"><label>{label}</label><span>{value}</span></div>
              ))}
            </div>
            <div className="nx-confirm-actions" style={{ marginTop: 14 }}>
              <Btn variant="ghost" onClick={() => setDeleteConfirm(detailModal)}>Remove</Btn>
              <Btn variant="ghost" onClick={() => setDisconnectConfirm(detailModal)}>Disconnect</Btn>
              <Btn variant="primary" onClick={() => setDetailModal(null)}>Close</Btn>
            </div>
          </TenancyModal>
        );
      })()}

      {/* ── Disconnect confirmation ── */}
      {disconnectConfirm && (
        <TenancyModal title="Disconnect Channel?" icon="WifiOff" onClose={() => setDisconnectConfirm(null)}>
          <p className="nx-confirm-desc">
            Disconnect <strong>{disconnectConfirm.name || disconnectConfirm.type}</strong>? The configuration is preserved — reconnect any time.
          </p>
          <div className="nx-confirm-actions">
            <Btn variant="ghost" onClick={() => setDisconnectConfirm(null)}>Cancel</Btn>
            <Btn variant="danger" icon="WifiOff" onClick={handleDisconnectConfirmed}>Disconnect</Btn>
          </div>
        </TenancyModal>
      )}

      {/* ── Remove confirmation ── */}
      {deleteConfirm && (
        <TenancyModal title="Remove Channel?" icon="Trash" danger onClose={() => setDeleteConfirm(null)}>
          <p className="nx-confirm-desc">This will permanently remove the channel configuration. You will need to reconfigure it to use it again.</p>
          <div className="nx-confirm-actions">
            <Btn variant="ghost" onClick={() => setDeleteConfirm(null)}>Cancel</Btn>
            <Btn variant="danger" icon="Trash" onClick={handleDelete}>Remove Channel</Btn>
          </div>
        </TenancyModal>
      )}
    </div>
  );
}
